// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Book, Bookmark, Settings,
  ArrowLeft, Copy, Check, Maximize, Minimize, List, X, Bot,
  Wifi, WifiOff
} from 'lucide-react';
import './Lecteur.css';
import './AdvancedReader.css';
import './accessibility.css';
import { apiJSON } from '../config/api';
import UnifiedReaderCore from './reader/UnifiedReaderCore';
import useFichierApi from '../hooks/useFichierApi';
import usePiegeFocus from '../hooks/usePiegeFocus';
import notificationService, { handleApiError as handleNotificationError } from '../utils/notificationService';
import KOReaderIntegration from './KOReaderIntegration';
import LoadingStates from './LoadingStates';
import { useAppContext } from '../context/AppContext';

const CONTENT_PAGE_LIMIT = 20;

/** MIME pour lecteur natif EPUB/PDF (aligné backend). */
function getBinaryMimeFromBook(book) {
  if (!book?.file_type) return null;
  const t = String(book.file_type).toLowerCase();
  if (t === 'pdf') return 'application/pdf';
  if (t === 'epub') return 'application/epub+zip';
  return null;
}

const UnifiedReader: React.FC = () => {
  // Lecture optionnelle de l'identifiant de livre depuis l'URL (/lecteur/:bookId)
  const { bookId } = useParams();
  const {
    streamHighlight,
    setStreamHighlight,
    setChatDraftQuery,
    setCurrentBook: setContextCurrentBook,
    chatDraftQuery,
  } = useAppContext();
  // États de base
  const [currentBook, setCurrentBook] = useState(null);
  const [bookContent, setBookContent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mode de lecture
  const [readerMode, setReaderMode] = useState('internal'); // 'internal', 'advanced', 'koreader'
  const [fontSize, setFontSize] = useState(16);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [fontFamily, setFontFamily] = useState('serif');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Panneaux et fonctionnalités
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showBookmarkPanel, setShowBookmarkPanel] = useState(false);

  const [highlightedChunks, setHighlightedChunks] = useState([]);
  /** Surlignage fin (chat / quote_span) : offsets dans le texte du chunk */
  const [streamRange, setStreamRange] = useState(null);

  // Annotations et bookmarks
  const [selectedText, setSelectedText] = useState('');
  const [annotations, setAnnotations] = useState([]);

  const [copiedText, setCopiedText] = useState(false);

  // Intégration IA (tiroir « Assistant RAG »)
  const [selectedTextForAI, setSelectedTextForAI] = useState('');
  const [immersiveTopBar, setImmersiveTopBar] = useState(false);
  const [selectionFab, setSelectionFab] = useState(null);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

  // Suivi du progrès de lecture
  const progressSaveTimeout = useRef(null);

  // États pour le mode offline
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Refs
  const readerContainerRef = useRef(null);
  const textViewerRef = useRef(null);

  const nativeMime =
    currentBook && readerMode !== 'koreader'
      ? getBinaryMimeFromBook(currentBook)
      : null;

  // Fichier source du lecteur natif, téléchargé AVEC l'en-tête d'authentification :
  // epub.js et react-pdf, à qui l'on passait l'URL, le récupéraient sans jeton (401).
  const fichierNatif = useFichierApi(
    nativeMime && currentBook?.book_id ? `books/${currentBook.book_id}/file` : null,
  );

  // Fichier original absent du stockage (404) : cas réel en production pour
  // les livres historiques, dont seul le texte a été importé. Plutôt qu'une
  // erreur bloquante, on bascule sur le texte extrait (`/api/book/{id}/content`,
  // déjà chargé par `loadBook`). Les autres erreurs (401, 5xx) restent
  // affichées : elles ne sont pas un état normal.
  const fichierOriginalIntrouvable = Boolean(nativeMime) && fichierNatif.status === 404;
  const modeNatif = Boolean(nativeMime) && !fichierOriginalIntrouvable;

  // ============================================================================
  // FONCTIONS POUR LE SUIVI DU PROGRÈS DE LECTURE
  // ============================================================================

  // Charger le progrès de lecture sauvegardé
  const loadReadingProgress = useCallback(async (bookId) => {
    if (!bookId) return;

    try {
      const progressData = await apiJSON(`books/${bookId}/progress`);

      if (progressData) {
        // Appliquer les paramètres de lecture sauvegardés
        if (progressData.font_size) setFontSize(progressData.font_size);
        if (progressData.line_height) setLineHeight(progressData.line_height);
        if (progressData.font_family) setFontFamily(progressData.font_family);
        if (progressData.theme) setIsDarkMode(progressData.theme === 'dark');
        if (progressData.zoom_level) {
          // Appliquer le zoom si nécessaire
        }

        // Retourner la page sauvegardée pour navigation
        return progressData.page_number || 1;
      }
    } catch (error) {
      console.warn('Erreur chargement progrès lecture:', error.message);
    }

    return 1; // Page par défaut
  }, []);

  // Sauvegarder le progrès de lecture avec debounce
  const saveReadingProgress = useCallback(async (bookId, progressData) => {
    if (!bookId || !progressData) return;

    // Annuler le timeout précédent
    if (progressSaveTimeout.current) {
      clearTimeout(progressSaveTimeout.current);
    }

    // Créer un nouveau timeout pour sauvegarder après 2 secondes d'inactivité
    progressSaveTimeout.current = setTimeout(async () => {
      try {
        const saveData = {
          position: progressData.position || 0,
          page_number: progressData.page_number || currentPage,
          scroll_position: progressData.scroll_position || 0.0,
          zoom_level: progressData.zoom_level || 1.0,
          font_size: progressData.font_size || fontSize,
          line_height: progressData.line_height || lineHeight,
          font_family: progressData.font_family || fontFamily,
          theme: progressData.theme || (isDarkMode ? 'dark' : 'light')
        };

        await apiJSON(`books/${bookId}/progress`, {
          method: 'PUT',
          body: JSON.stringify(saveData)
        });

        console.log('Progrès de lecture sauvegardé:', saveData);
      } catch (error) {
        console.error('Erreur sauvegarde progrès lecture:', error);
        notificationService.error('Erreur sauvegarde progrès de lecture');
      }
    }, 2000); // 2 secondes de délai
  }, [currentPage, fontSize, lineHeight, fontFamily, isDarkMode]);

  // Fonction pour déclencher la sauvegarde immédiate
  const saveProgressNow = useCallback(async (bookId) => {
    if (progressSaveTimeout.current) {
      clearTimeout(progressSaveTimeout.current);
    }

    if (bookId && currentBook) {
      await saveReadingProgress(bookId, {
        position: 0, // À calculer selon la position dans le texte
        page_number: currentPage,
        scroll_position: textViewerRef.current?.scrollTop || 0,
        zoom_level: 1.0,
        font_size: fontSize,
        line_height: lineHeight,
        font_family: fontFamily,
        theme: isDarkMode ? 'dark' : 'light'
      });
    }
  }, [currentPage, fontSize, lineHeight, fontFamily, isDarkMode, currentBook, saveReadingProgress]);

  // ============================================================================
  // FONCTIONS POUR LE MODE OFFLINE
  // ============================================================================

  // Vérifier la connectivité
  const checkOnlineStatus = useCallback(() => {
    const online = navigator.onLine;
    setIsOnline(online);
    return online;
  }, []);

  // Les métadonnées des livres ne sont plus rangées dans Cache Storage
  // (`calibre-rag-books-v1.0.0`) : c'étaient des réponses de l'API, propres au
  // compte connecté, qu'un autre compte du même navigateur pouvait relire. Le
  // service worker purge ce cache à son activation (voir swRoutage.js).

  // Charger un livre avec gestion offline
  const loadBook = useCallback(async (bookId, highlightOptions = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Hors ligne, l'application s'ouvre (enveloppe en cache) mais les
      // données de la bibliothèque, authentifiées, ne sont jamais conservées.
      if (!checkOnlineStatus()) {
        throw new Error('Livre non disponible hors ligne : reconnectez-vous au réseau pour le lire.');
      }

      const booksData = await apiJSON('books/structured');
      const book = booksData.books?.find(b => b.book_id === bookId);

      if (!book) {
        throw new Error('Livre non trouvé');
      }

      setCurrentBook(book);

      // Charger le progrès de lecture sauvegardé
      const savedPage = await loadReadingProgress(bookId);

      // Charger le contenu selon le mode
      if (readerMode === 'koreader') {
        // KOReader gérera le contenu
        return;
      }

      // Utiliser la page sauvegardée ou la page demandée dans les options
      let startPage = highlightOptions.page || savedPage;
      if (highlightOptions.chunkIdx != null && highlightOptions.page == null) {
        startPage = Math.max(
          1,
          Math.floor(Number(highlightOptions.chunkIdx) / CONTENT_PAGE_LIMIT) + 1
        );
      }
      const contentData = await apiJSON(
        `book/${bookId}/content?page=${startPage}&limit=${CONTENT_PAGE_LIMIT}`
      );

      if (contentData.error) {
        throw new Error(contentData.error);
      }

      setBookContent(contentData);
      setTotalPages(contentData.total_pages);
      setCurrentPage(startPage);

      // Charger les annotations existantes
      try {
        const annotationsData = await apiJSON(`book/${bookId}/annotations`);
        if (annotationsData.annotations) {
          setAnnotations(annotationsData.annotations);
        }
      } catch (error) {
        console.warn('Annotations non disponibles:', error.message);
      }

      // Gérer les options de surbrillance (chunk_idx côté API)
      if (highlightOptions.chunkId != null && highlightOptions.chunkId !== undefined) {
        setHighlightedChunks([highlightOptions.chunkId]);
        setCurrentPage(highlightOptions.page || 1);
        setTimeout(() => {
          const chunkElement = document.querySelector(
            `[data-chunk-idx="${highlightOptions.chunkId}"], [data-chunk-id="${highlightOptions.chunkId}"]`
          );
          chunkElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
      if (highlightOptions.chunkIdx != null && highlightOptions.chunkIdx !== undefined) {
        const cidx = Number(highlightOptions.chunkIdx);
        setHighlightedChunks([cidx]);
        setStreamRange({
          chunkIdx: cidx,
          charStart: highlightOptions.charStart ?? 0,
          charEnd: highlightOptions.charEnd ?? 0,
        });
        setTimeout(() => {
          document
            .querySelector(`[data-chunk-idx="${cidx}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }

    } catch (error) {
      console.error('Erreur chargement livre:', error);
      setError(error.message);
      handleNotificationError(error, 'Chargement du livre');
    } finally {
      setLoading(false);
    }
  }, [readerMode, checkOnlineStatus, loadReadingProgress]);

  useEffect(() => {
    window.openCitationInReader = (citation) => {
      setStreamHighlight({
        bookId: String(citation.book_id),
        chunkIdx: Number(citation.chunk_idx),
        charStart: citation.char_start ?? 0,
        charEnd: citation.char_end ?? 0,
        requestId: Date.now(),
      });
    };
    return () => {
      if (window.openCitationInReader) {
        delete window.openCitationInReader;
      }
    };
  }, [setStreamHighlight]);

  useEffect(() => {
    if (!streamHighlight) {
      return;
    }
    const { bookId: hid, chunkIdx, charStart, charEnd } = streamHighlight;
    const targetPage = Math.max(
      1,
      Math.floor(Number(chunkIdx) / CONTENT_PAGE_LIMIT) + 1
    );
    let cancelled = false;
    (async () => {
      try {
        if (!currentBook || currentBook.book_id !== hid) {
          await loadBook(hid, {
            page: targetPage,
            chunkIdx,
            charStart,
            charEnd,
          });
          return;
        }
        if (currentPage !== targetPage) {
          setLoading(true);
          try {
            const contentData = await apiJSON(
              `book/${hid}/content?page=${targetPage}&limit=${CONTENT_PAGE_LIMIT}`
            );
            if (cancelled) return;
            setBookContent(contentData);
            setCurrentPage(targetPage);
          } finally {
            setLoading(false);
          }
        }
        if (cancelled) return;
        setHighlightedChunks([Number(chunkIdx)]);
        setStreamRange({
          chunkIdx: Number(chunkIdx),
          charStart: charStart ?? 0,
          charEnd: charEnd ?? 0,
        });
        setTimeout(() => {
          if (cancelled) return;
          document
            .querySelector(`[data-chunk-idx="${chunkIdx}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 400);
      } catch (e) {
        console.warn('streamHighlight lecteur:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- une seule application par nouveau streamHighlight
  }, [streamHighlight]);

  // Écouteurs d'événements pour la connectivité
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('🌐 Connexion internet rétablie');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('📴 Mode hors ligne activé');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Chargement initial et gestion des changements d'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlBookId = urlParams.get('book_id') || urlParams.get('book') || bookId;
    const urlMode = urlParams.get('mode');
    const chunkId = urlParams.get('chunk');
    const highlight = urlParams.get('highlight') === 'true';
    const charStart = urlParams.get('cs');
    const charEnd = urlParams.get('ce');
    const page = urlParams.get('page');

    if (urlMode && urlMode !== readerMode) {
      setReaderMode(urlMode);
      // Le changement de readerMode va recréer loadBook, ce qui déclenchera cet effet à nouveau
      // mais avec urlMode === readerMode, donc on s'arrête là.
      return;
    }

    if (urlBookId) {
      loadBook(urlBookId, {
        chunkId: chunkId ? parseInt(chunkId) : null,
        highlight,
        charStart: charStart ? parseInt(charStart) : null,
        charEnd: charEnd ? parseInt(charEnd) : null,
        page: page ? parseInt(page) : null
      });
    }
  }, [bookId, loadBook, readerMode]);

  // Contrôles de navigation
  const goToPage = useCallback(async (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages || !currentBook) return;

    try {
      setLoading(true);
      const contentData = await apiJSON(`book/${currentBook.book_id}/content?page=${pageNum}&limit=20`);
      setBookContent(contentData);
      setCurrentPage(pageNum);

      // Sauvegarder le progrès de lecture
      saveReadingProgress(currentBook.book_id, {
        page_number: pageNum,
        scroll_position: 0.0
      });
    } catch (error) {
      handleNotificationError(error, 'Navigation');
    } finally {
      setLoading(false);
    }
  }, [currentBook, totalPages, saveReadingProgress]);

  // Sauvegarder le progrès lors des changements de paramètres de lecture
  useEffect(() => {
    if (currentBook && (fontSize || lineHeight || fontFamily || isDarkMode !== undefined)) {
      saveReadingProgress(currentBook.book_id, {
        font_size: fontSize,
        line_height: lineHeight,
        font_family: fontFamily,
        theme: isDarkMode ? 'dark' : 'light'
      });
    }
  }, [fontSize, lineHeight, fontFamily, isDarkMode, currentBook, saveReadingProgress]);

  // Sauvegarder le progrès avant de quitter la page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentBook) {
        saveProgressNow(currentBook.book_id);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Sauvegarder une dernière fois lors du démontage du composant
      if (currentBook) {
        saveProgressNow(currentBook.book_id);
      }
    };
  }, [currentBook, saveProgressNow]);

  // Sélection de texte et annotations
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection.toString().trim();

    if (text.length > 0) {
      setSelectedText(text);
      setSelectedTextForAI(text);
        try {
        const range = selection.rangeCount ? selection.getRangeAt(0) : null;
        if (range) {
          const rect = range.getBoundingClientRect();
          setSelectionFab({
            top: Math.max(8, rect.top - 44),
            left: Math.min(
              window.innerWidth - 148,
              Math.max(8, rect.left + rect.width / 2 - 74)
            ),
          });
        }
      } catch {
        setSelectionFab(null);
      }
    } else {
      setSelectedText('');
      setSelectionFab(null);
    }
  }, []);

  // Sélection de texte (mode chunks uniquement — EPUB/PDF gèrent leur propre sélection)
  useEffect(() => {
    if (modeNatif) return undefined;
    const handleMouseUp = () => {
      setTimeout(handleTextSelection, 100);
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [handleTextSelection, modeNatif]);

  useEffect(() => {
    if (currentBook) {
      setContextCurrentBook(currentBook);
      document.body.classList.add('reader-immersive-open');
    }
    return () => {
      document.body.classList.remove('reader-immersive-open');
    };
  }, [currentBook, setContextCurrentBook]);

  useEffect(() => {
    const onMove = (e) => {
      setImmersiveTopBar(e.clientY < 72);
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    return () => document.removeEventListener('mousemove', onMove);
  }, []);

  // Copier le texte sélectionné
  const copySelectedText = async () => {
    if (selectedText) {
      try {
        await navigator.clipboard.writeText(selectedText);
        setCopiedText(true);
        notificationService.showToast('Texte copié !');
        setTimeout(() => setCopiedText(false), 2000);
      } catch (error) {
        handleNotificationError(error, 'Copie du texte');
      }
    }
  };

  const openAIDrawerFromSelection = useCallback(() => {
    if (!selectedTextForAI || !currentBook) return;
    const prompt = `En te basant sur ce livre (« ${currentBook.title || ''} »), peux-tu m'expliquer ce passage : « ${selectedTextForAI} » ?`;
    setChatDraftQuery(prompt);
    setAiDrawerOpen(true);
    setSelectionFab(null);
  }, [selectedTextForAI, currentBook, setChatDraftQuery]);

  const handleAskAIFromNative = useCallback(
    (text, pageContext) => {
      if (!text?.trim() || !currentBook) return;
      setSelectedTextForAI(text);
      setSelectedText(text);
      let extra = '';
      if (pageContext?.type === 'pdf' && pageContext.page != null) {
        extra = ` (page PDF ${pageContext.page}${
          pageContext.numPages ? `/${pageContext.numPages}` : ''
        })`;
      } else if (pageContext?.type === 'epub') {
        extra = ' (EPUB)';
      }
      const prompt = `En te basant sur ce livre (« ${
        currentBook.title || ''
      } »)${extra}, peux-tu m'expliquer ce passage : « ${text} » ?`;
      setChatDraftQuery(prompt);
      setAiDrawerOpen(true);
      setSelectionFab(null);
    },
    [currentBook, setChatDraftQuery],
  );

  // Expliquer avec l'IA (navigation pleine page ou tiroir)
  const explainWithAI = (autoSend = false) => {
    if (selectedText) {
      if (autoSend) {
        localStorage.setItem('selectedTextForAI', selectedText);
        localStorage.setItem('autoSendAI', 'true');
      } else {
        localStorage.setItem('selectedTextForAI', selectedText);
      }
      const aiUrl = `/ia?book=${encodeURIComponent(currentBook?.book_id || '')}&text=${encodeURIComponent(selectedText)}${autoSend ? '&auto=true' : ''}`;
      window.location.href = aiUrl;
    }
  };

  const renderChunkText = (chunk) => {
    const raw = chunk.text ?? chunk.content ?? '';
    const idx = chunk.chunk_idx;
    if (
      streamRange != null &&
      Number(streamRange.chunkIdx) === Number(idx) &&
      streamRange.charEnd > streamRange.charStart
    ) {
      const a = Math.max(0, Math.min(streamRange.charStart, raw.length));
      const b = Math.max(a, Math.min(streamRange.charEnd, raw.length));
      if (a < b) {
        return (
          <>
            {raw.slice(0, a)}
            <span className="highlight-active-stream">{raw.slice(a, b)}</span>
            {raw.slice(b)}
          </>
        );
      }
    }
    return raw;
  };

  // Rendu du contenu selon le mode
  const renderContent = () => {
    if (readerMode === 'koreader') {
      return (
        <div className="koreader-mode">
          <h2>Mode KOReader</h2>
          <p>Ouverture avec KOReader externe...</p>
          {currentBook && (
            <KOReaderIntegration
              bookId={currentBook.book_id}
              filePath={currentBook.file_path}
              fileName={currentBook.title}
            />
          )}
        </div>
      );
    }

    if (!bookContent) return null;

    return (
      <div
        ref={textViewerRef}
        className={`reader-text-viewer ${readerMode === 'advanced' ? 'advanced-mode' : ''}`}
        style={{
          fontSize: `${fontSize}px`,
          lineHeight,
          fontFamily,
          backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
          color: isDarkMode ? '#e0e0e0' : '#333333'
        }}
        onMouseUp={handleTextSelection}
      >
        {bookContent.chunks?.map((chunk, index) => (
          <div
            key={chunk.chunk_idx ?? chunk.chunk_id ?? index}
            data-chunk-idx={chunk.chunk_idx}
            data-chunk-id={chunk.chunk_id ?? chunk.chunk_idx}
            className={`content-chunk ${highlightedChunks.includes(chunk.chunk_idx) ? 'highlighted' : ''}`}
          >
            <div className="chunk-content">
              {renderChunkText(chunk)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Tiroir « Assistant RAG » : dialogue modal, focus piégé, Échap ferme et le
  // focus revient à l'élément qui l'a ouvert.
  const fermerTiroirIA = useCallback(() => setAiDrawerOpen(false), []);
  const tiroirIARef = usePiegeFocus(aiDrawerOpen, fermerTiroirIA);

  // États de chargement et d'erreur
  if (loading && !currentBook) {
    return (
      <div className="reader-loading">
        <Book size={48} />
        <p>Chargement du livre...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reader-error">
        <Book size={48} />
        <h2>Erreur de chargement</h2>
        <p>{error}</p>
        <button onClick={() => window.history.back()}>
          Retour à la bibliothèque
        </button>
      </div>
    );
  }

  if (!currentBook) {
    return (
      <div className="reader-empty">
        <Book size={48} />
        <h2>Aucun livre sélectionné</h2>
        <p>Sélectionnez un livre depuis la bibliothèque pour commencer la lecture.</p>
      </div>
    );
  }

  return (
    <div
      ref={readerContainerRef}
      className={`unified-reader unified-reader--immersive ${isDarkMode ? 'dark-mode' : ''} ${isFullscreen ? 'fullscreen' : ''} mode-${readerMode}`}
    >
      <div className={`reader-immersive-topbar ${immersiveTopBar ? 'is-visible' : ''}`}>
        <div className="reader-immersive-topbar-inner">
          <button type="button" onClick={() => window.history.back()} className="nav-btn glass-button">
            <ArrowLeft size={20} />
            Retour à la bibliothèque
          </button>
          {currentBook && (
            <span className="reader-immersive-title">{currentBook.title}</span>
          )}
          <div className="reader-immersive-actions">
            <label className="reader-font-inline">
              Police
              <input
                type="range"
                min={12}
                max={26}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
              />
            </label>
            <button type="button" className="control-btn glass-button" onClick={() => setShowTableOfContents(!showTableOfContents)} title="Sommaire">
              <List size={20} />
            </button>
            {/* Le panneau d'annotations existait et était alimenté, mais son
                bouton se trouvait dans une barre d'outils jamais montée : les
                annotations étaient donc chargées, stockées, relues par l'API…
                et inatteignables depuis l'interface. */}
            <button
              type="button"
              className="control-btn glass-button"
              onClick={() => setShowBookmarkPanel(!showBookmarkPanel)}
              title="Annotations"
              aria-label="Annotations"
              aria-pressed={showBookmarkPanel}
            >
              <Bookmark size={20} />
            </button>
            <button type="button" className="control-btn glass-button" onClick={() => setIsDarkMode(!isDarkMode)} title="Thème clair / sombre">
              {isDarkMode ? 'Clair' : 'Sombre'}
            </button>
            <button
              type="button"
              className="control-btn reader-ia-page-btn glass-button"
              onClick={() => {
                const q = `Pose une question sur la page actuelle du livre « ${currentBook?.title || ''} » (page ${currentPage}).`;
                setChatDraftQuery(q);
                setAiDrawerOpen(true);
              }}
              title="Question IA sur cette page"
            >
              <Bot size={20} />
              IA page
            </button>
          </div>
        </div>
      </div>

      {/* Header classique (secours si besoin) — masqué en mode immersif */}
      <div className="reader-header reader-header--legacy">
        <div className="reader-nav">
          <button type="button" onClick={() => window.history.back()} className="nav-btn glass-button">
            <ArrowLeft size={20} />
            Retour
          </button>

          {currentBook && (
            <div className="book-info">
              <h2>{currentBook.title}</h2>
              <span className="author">{currentBook.author}</span>
            </div>
          )}
        </div>

        <div className="reader-controls glass-panel">
          <div className={`offline-indicator ${!isOnline ? 'offline' : ''}`}>
            {!isOnline ? <WifiOff size={16} /> : <Wifi size={16} />}
            <span className="offline-text">
              {!isOnline ? 'Hors ligne' : 'En ligne'}
            </span>
          </div>

          <button type="button" onClick={() => setShowTableOfContents(!showTableOfContents)} className="control-btn glass-button">
            <List size={20} />
          </button>

          <button type="button" onClick={() => setShowSettings(!showSettings)} className="control-btn glass-button">
            <Settings size={20} />
          </button>

          <button type="button" onClick={() => setIsFullscreen(!isFullscreen)} className="control-btn glass-button">
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </button>
        </div>
      </div>

      <div className="reader-content">
        {/* Panneau de table des matières */}
        {showTableOfContents && (
          <div className="toc-panel">
            <h3>Table des matières</h3>
            {/* Contenu de la table des matières */}
          </div>
        )}

        {/* Annotations : elles étaient chargées depuis book/{id}/annotations mais
            aucun panneau ne les affichait — le bouton ne faisait que basculer un
            état invisible. */}
        {showBookmarkPanel && (
          <div className="toc-panel" data-testid="panneau-annotations">
            <h3>Annotations</h3>
            {annotations.length === 0 ? (
              <p style={{ fontSize: 13, color: '#64748b' }} data-testid="annotations-vide">
                Aucune annotation pour ce livre. Sélectionnez du texte pour en créer une.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
                {annotations.map((annotation) => (
                  <li
                    key={annotation.id}
                    data-testid="annotation"
                    style={{ borderLeft: '3px solid #8B5CF6', paddingLeft: 8 }}
                  >
                    {annotation.text && (
                      <blockquote style={{ margin: 0, fontSize: 13, fontStyle: 'italic', color: '#475569' }}>
                        « {String(annotation.text).slice(0, 160)} »
                      </blockquote>
                    )}
                    {annotation.note && (
                      <div style={{ marginTop: 4, fontSize: 13 }}>{annotation.note}</div>
                    )}
                    {annotation.page_start ? (
                      <button
                        type="button"
                        onClick={() => goToPage(annotation.page_start)}
                        style={{ marginTop: 6, fontSize: 12 }}
                      >
                        Aller à la page {annotation.page_start}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Zone de lecture principale */}
        <div className={`reading-area${modeNatif ? ' reading-area--native' : ''}`}>
          {loading ? (
            <LoadingStates.Loading message="Chargement du livre..." />
          ) : error ? (
            <LoadingStates.Error
              message={error}
              onRetry={() => loadBook(currentBook?.book_id)}
            />
          ) : modeNatif && currentBook && fichierNatif.error ? (
            <LoadingStates.Error
              message={fichierNatif.error}
              onRetry={fichierNatif.retry}
            />
          ) : modeNatif && currentBook && !fichierNatif.data ? (
            <LoadingStates.Loading message="Chargement du fichier..." />
          ) : modeNatif && currentBook ? (
            <div className="text-content unified-reader-native-shell">
              <UnifiedReaderCore
                fileData={fichierNatif.data}
                mimeType={nativeMime}
                onAskAI={handleAskAIFromNative}
              />
            </div>
          ) : bookContent ? (
            /* `renderContent()` est l'implémentation maintenue : elle parcourt
               `bookContent.chunks`, applique la surbrillance et branche la
               sélection de texte nécessaire aux annotations.
               Le bloc précédent rendait `bookContent.content`, un champ que
               `/api/book/{id}/content` ne renvoie pas — il expose `chunks`. Le
               lecteur affichait donc l'en-tête et les commandes, mais AUCUN
               texte, quel que soit le livre. */
            <>
              {fichierOriginalIntrouvable && (
                <p className="reader-repli-texte" role="status" data-testid="repli-texte">
                  Fichier original indisponible : affichage du texte extrait
                </p>
              )}
              <div className="text-content">{renderContent()}</div>
            </>
          ) : (
            <LoadingStates.Empty
              message="Sélectionnez un livre pour commencer la lecture"
              action={
                <button onClick={() => window.location.href = '/'} className="select-book-btn glass-button">
                  Choisir un livre
                </button>
              }
            />
          )}
        </div>

      </div>

      {/* Panneau de paramètres */}
      {showSettings && (
        <div className="settings-panel">
          <h3>Paramètres de lecture</h3>

          <div className="setting-group">
            <label>Taille de police</label>
            <input
              type="range"
              min="12"
              max="24"
              value={fontSize}
              onChange={(e) => setFontSize(parseInt(e.target.value))}
            />
            <span>{fontSize}px</span>
          </div>

          <div className="setting-group">
            <label>Interligne</label>
            <input
              type="range"
              min="1.2"
              max="2.0"
              step="0.1"
              value={lineHeight}
              onChange={(e) => setLineHeight(parseFloat(e.target.value))}
            />
            <span>{lineHeight}</span>
          </div>

          <div className="setting-group">
            <label>Police</label>
            <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
              <option value="serif">Serif</option>
              <option value="sans-serif">Sans-serif</option>
              <option value="monospace">Monospace</option>
            </select>
          </div>

          <div className="setting-group">
            <label>
              <input
                type="checkbox"
                checked={isDarkMode}
                onChange={(e) => setIsDarkMode(e.target.checked)}
              />
              Mode sombre
            </label>
          </div>
        </div>
      )}

      {selectionFab && selectedTextForAI && !modeNatif && (
        <button
          type="button"
          className="reader-selection-fab"
          style={{ top: selectionFab.top, left: selectionFab.left }}
          onClick={openAIDrawerFromSelection}
        >
          <Bot size={16} />
          Explique-moi ceci
        </button>
      )}

      {selectedText && !modeNatif && (
        <div className="text-selection-toolbar">
          <button type="button" onClick={copySelectedText}>
            {copiedText ? <Check size={16} /> : <Copy size={16} />}
            {copiedText ? 'Copié' : 'Copier'}
          </button>
          <div className="ai-options">
            <button type="button" onClick={() => explainWithAI(false)} className="ai-explain-btn glass-button">
              <Bot size={16} />
              Expliquer avec l&apos;IA
            </button>
            <button type="button" onClick={() => explainWithAI(true)} className="ai-explain-btn auto-send glass-button">
              <Bot size={16} />
              Expliquer &amp; Envoyer
            </button>
          </div>
          <button type="button" onClick={() => { setSelectedText(''); setSelectionFab(null); }}>
            Annuler
          </button>
        </div>
      )}

      {aiDrawerOpen && (
        <>
          {/* Fond cliquable pour la souris ; hors de l'ordre de tabulation, le
              clavier ferme par Échap ou par le bouton du tiroir. */}
          <button
            type="button"
            className="reader-ai-drawer-backdrop"
            aria-label="Fermer l'assistant"
            tabIndex={-1}
            onClick={fermerTiroirIA}
          />
          <aside
            ref={tiroirIARef}
            className="reader-ai-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reader-ai-drawer-title"
            tabIndex={-1}
          >
            <div className="reader-ai-drawer-head">
              <h3 id="reader-ai-drawer-title">Assistant RAG</h3>
              <button
                type="button"
                className="close-btn glass-button"
                onClick={fermerTiroirIA}
                aria-label="Fermer l'assistant"
              >
                <X size={20} aria-hidden />
              </button>
            </div>
            <p className="reader-ai-drawer-hint">
              Le texte ci-dessous est synchronisé avec l&apos;onglet IA. Ouvrez l&apos;IA pour lancer la conversation contextuelle sur ce livre.
            </p>
            <textarea
              className="reader-ai-drawer-textarea"
              aria-label="Question préparée pour l'IA"
              rows={6}
              readOnly
              value={chatDraftQuery || ''}
            />
            <div className="reader-ai-drawer-actions">
              <button
                type="button"
                className="ask-btn glass-button"
                onClick={() => {
                  window.location.href = `/ia?book=${encodeURIComponent(currentBook?.book_id || '')}`;
                }}
              >
                Ouvrir l&apos;IA (RAG)
              </button>
            </div>
          </aside>
        </>
      )}

      <style>{`
        .unified-reader {
          display: flex;
          flex-direction: column;
          height: 100vh;
          background: #ffffff;
        }

        .unified-reader.fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
        }

        .reader-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .reader-nav {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .nav-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: none;
          border-radius: 6px;
          background: #8B5CF6;
          color: white;
          cursor: pointer;
          font-size: 14px;
        }

        .book-info h2 {
          margin: 0;
          font-size: 18px;
          color: #1f2937;
        }

        .author {
          font-size: 14px;
          color: #6b7280;
        }

        .reader-controls {
          display: flex;
          gap: 8px;
        }

        .control-btn {
          padding: 8px;
          border: none;
          border-radius: 6px;
          background: #e5e7eb;
          cursor: pointer;
          color: #374151;
        }

        .reader-content {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        .toc-panel {
          width: 250px;
          border-right: 1px solid #e5e7eb;
          padding: 16px;
          background: #f9fafb;
        }

        .reading-area {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          position: relative;
        }

        .reader-repli-texte {
          max-width: 800px;
          margin: 0 auto 16px;
          padding: 8px 12px;
          border-left: 3px solid #f59e0b;
          border-radius: 4px;
          background: #fffbeb;
          color: #92400e;
          font-size: 13px;
        }

        .text-content {
          max-width: 800px;
          margin: 0 auto;
          line-height: inherit;
          user-select: text;
        }

        .ai-panel {
          width: 350px;
          border-left: 1px solid #e5e7eb;
          background: #f9fafb;
          display: flex;
          flex-direction: column;
        }

        .ai-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .ai-panel-header h3 {
          margin: 0;
          font-size: 16px;
        }

        .close-btn {
          padding: 4px;
          border: none;
          background: none;
          cursor: pointer;
          color: #6b7280;
        }

        .ai-panel-content {
          flex: 1;
          padding: 16px;
          overflow-y: auto;
        }

        .selected-text {
          margin-bottom: 16px;
          padding: 12px;
          background: #e5e7eb;
          border-radius: 6px;
        }

        .selected-text h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
        }

        .selected-text p {
          margin: 0;
          font-style: italic;
          color: #374151;
        }

        .ai-question {
          margin-bottom: 16px;
        }

        .ai-question textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          resize: vertical;
          font-family: inherit;
        }

        .ask-btn {
          width: 100%;
          padding: 8px;
          margin-top: 8px;
          border: none;
          border-radius: 4px;
          background: #8B5CF6;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .ask-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .ai-response {
          padding: 12px;
          background: #dbeafe;
          border-radius: 6px;
        }

        .ai-response h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
        }

        .response-content {
          color: #1e40af;
          line-height: 1.5;
        }

        .settings-panel {
          position: absolute;
          top: 80px;
          right: 16px;
          width: 300px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .setting-group {
          margin-bottom: 16px;
        }

        .setting-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .setting-group input[type="range"] {
          width: 100%;
          margin-right: 8px;
        }

        .setting-group select {
          width: 100%;
          padding: 4px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
        }

        .select-book-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          background: #8B5CF6;
          color: white;
          cursor: pointer;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default UnifiedReader;
